<template>
  <div
    class="product-review-stars"
    @click="scrollToReviews"
  >
    <div
      v-if="rating && ratingCount"
      class="product-review-stars__wrapper"
    >
      <div class="product-review-stars__content">
        <div
          v-for="(star, index) in 5"
          :key="index"
          class="product-review-stars__star"
        >
          <component
            :is="allocateStarStatus(index + 1)"
          />
        </div>
      </div>

      <!-- Amount of reviews -->
      <p
        class="text-body-2xs product-review-stars__number"
        v-text="`(${ratingCount})`"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product review stars (product-review-stars)
 * -----------------------------------------------------------------------------
 * Widget for displaying Shopify product reviews.
 *
 * @param {Boolean, Object} product - Product object or false if not yet loaded.
 * @param {Object} productReviews - Product reviews object.
 * @param {Number} reviewCount - Number of total reviews that exist.
 */
import IconStar from '~icons/misc/star.svg'
import IconStarFilled from '~icons/misc/star-filled.svg'

export default {
  name: 'ProductReviewStars',

  components: {
    IconStar,
    IconStarFilled,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },
    productReviews: {
      type: Object,
      default: () => {},
    },
    reviewCount: {
      type: [Boolean, Number],
      default: false,
    },
  },

  data() {
    return {
      rating: null,
      ratingCount: null,
    }
  },

  watch: {

    /**
     * Watch the product to update the reviews data.
     */
    product: {
      deep: true,
      handler() {
        this.setRating()
        this.setRatingCount()
      },
    },
  },

  mounted() {

    /**
     * Use fallback data when the component is first mounted to avoid flickering
     * before Vue loads in.
     */
    this.setRating(this.productReviews.rating.value)
    this.setRatingCount(this.productReviews.number)
  },

  methods: {

    /**
     * Sets review count from current API response OR falls back to given number.
     * @param {Number} fallback - Number of product reviews.
     */
    setRatingCount(fallback) {
      if (!this.reviewCount && fallback) {
        this.ratingCount = fallback
        return
      } else if (!this.reviewCount) {
        this.ratingCount = false
        return
      }

      this.ratingCount = this.reviewCount
    },

    /**
     * Sets rating from current API response OR falls back to given number.
     * @param [Number] fallback - Product rating.
     */
    setRating(fallback) {
      const rating =
        this.product?.metafields?.find(
          field => field.name === 'review_rating',
        )

      if (!rating?.value && fallback) {
        this.rating = fallback
        return
      }else if (!rating?.value) {
        this.rating = false
        return
      }

      this.rating = JSON.parse(rating.value).value
    },

    /**
     * Calculates how many stars to display as filled or empty.
     * @param {Number} index - Index of the star in the for loop.
     */
    allocateStarStatus(index) {
      const rating = parseFloat(this.rating)

      if (rating >= index) {
        return 'icon-star-filled'
      } else if (rating >= (index - 0.5)) {
        return 'icon-star-filled'
      }

      return 'icon-star'
    },

    /**
     * Scrolls the reviews into view.
     */
     scrollToReviews() {
      const reviews = document.querySelector('[js-reviews-section="section"]')

      if (!reviews) {
        return
      }

      reviews.scrollIntoView(
        {
          block: "start",
          inline: "nearest",
          behaviour: 'smooth',
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import './product-review-stars';
</style>
